import { createContext, useContext } from 'react';
import { setCookie } from 'cookies-next';
import { createStore, useStore } from 'zustand';

import type { DecoratedSession } from 'types/infrastructure/session';

interface SessionState {
  session: DecoratedSession | null;
  initialDeployedSha: string | null;
  isLoggingOut: boolean;
  sessionExpiresAt: Date | null;
  setSession: (session: DecoratedSession | null) => void;
  setSessionExpiration: (expiresAt: Date | null) => void;
  setIsLoggingOut: (isLoggingOut: boolean) => void;
  hasNewRelease: () => boolean;
  isSessionExpired: () => boolean;
}

type SessionStore = ReturnType<typeof createSessionStore>;

export const createSessionStore = (session?: DecoratedSession) => {
  const initialSha = session?.deployedSha ?? null;

  return createStore<SessionState>()((set, get) => ({
    session: session ?? null,
    initialDeployedSha: initialSha,
    isLoggingOut: false,
    sessionExpiresAt: null,
    setSession: (session) => {
      if (!session) {
        set({ session: null, sessionExpiresAt: null });
        return;
      }

      set({ session });
      setCookie('developer_id', session.developerId);
    },
    setSessionExpiration: (expiresAt) => {
      set({ sessionExpiresAt: expiresAt });
    },
    setIsLoggingOut: (isLoggingOut) => {
      set({ isLoggingOut });
    },
    hasNewRelease: () => {
      const state = get();
      const currentSha = state.session?.deployedSha;
      const initialSha = state.initialDeployedSha;

      // Only return true if both SHAs exist and they differ
      return !!(initialSha && currentSha && initialSha !== currentSha);
    },
    isSessionExpired: () => {
      const state = get();
      const expiresAt = state.sessionExpiresAt;
      if (!expiresAt) return false;
      // Add a 30-second buffer to account for clock drift and network latency
      return new Date() >= new Date(expiresAt.getTime() - 30000);
    },
  }));
};

export const SessionContext = createContext<SessionStore | null>(null);

export const useSessionContext = <T>(selector: (state: SessionState) => T): T => {
  const store = useContext(SessionContext);
  if (!store) {
    throw new Error('Missing SessionContext.Provider in the tree');
  }
  return useStore(store, selector);
};

// Legacy export for backward compatibility
export const useSessionStore = () => {
  const store = useContext(SessionContext);
  if (!store) {
    throw new Error('Missing SessionContext.Provider in the tree');
  }
  return {
    session: useStore(store, (state) => state.session),
    setSession: useStore(store, (state) => state.setSession),
  };
};
